import { getCurrentUserId } from "../../auth/currentUser";
import axios from "axios";
import { GridRowId } from "@mui/x-data-grid-premium";
import {
  CRUDResponse,
  BulkStringOperationConfig,
  BulkUpdateResponse,
} from "./types";

export interface CRUDConfig {
  apiUrl: string;
  submitOnlyChangedFields: boolean;
  getRowId: (row: any) => string | number;
  onBeforeUpdate?: (
    originalRow: any,
    updatedRow: any,
    changedFields: Record<string, any>
  ) => any;
}

export interface UpdateResult {
  success: boolean;
  data?: any;
  error?: string;
}

/**
 * Handles CRUD operations for ServerDataGrid
 * Encapsulates the logic for creating, reading, updating, and deleting records
 */
export class CRUDHandler {
  private config: CRUDConfig;

  constructor(config: CRUDConfig) {
    this.config = config;
  }

  /**
   * Determine the ID field name from the row data
   */
  private getIdFieldName(row: any): string {
    const idValue = this.config.getRowId(row);

    if (typeof idValue === "string" && row[idValue] !== undefined) {
      return idValue;
    }

    // Find the field that matches the ID value
    const idField = Object.keys(row).find(
      (key) => this.config.getRowId(row) === row[key]
    );

    return idField || "id";
  }

  /**
   * Normalize the updated row by converting flat dot-notation fields into nested structure
   * This ensures the updated row format matches the original row format for comparison
   */
  private normalizeUpdatedRow(updatedRow: any): any {
    const normalized: any = {};

    // First, copy all non-dot-notation properties
    Object.keys(updatedRow).forEach((key) => {
      if (!key.includes(".")) {
        normalized[key] = updatedRow[key];
      }
    });

    // Then, handle dot-notation properties by converting them to nested structure
    Object.keys(updatedRow).forEach((key) => {
      if (key.includes(".")) {
        const parts = key.split(".");
        const [parentKey, ...childParts] = parts;
        const childKey = childParts.join(".");

        // Initialize parent object if it doesn't exist
        if (
          !normalized[parentKey] ||
          typeof normalized[parentKey] !== "object"
        ) {
          normalized[parentKey] = {};
        }

        // Set the nested value
        if (childParts.length === 1) {
          // Simple nested property: supply.supply_title
          normalized[parentKey][childKey] = updatedRow[key];
        } else {
          // Deeper nesting: supply.details.name (if needed in the future)
          this.setNestedValue(
            normalized[parentKey],
            childParts,
            updatedRow[key]
          );
        }
      }
    });

    return normalized;
  }

  /**
   * Helper to set deeply nested values
   */
  private setNestedValue(obj: any, path: string[], value: any): void {
    const [first, ...rest] = path;
    if (rest.length === 0) {
      obj[first] = value;
    } else {
      if (!obj[first] || typeof obj[first] !== "object") {
        obj[first] = {};
      }
      this.setNestedValue(obj[first], rest, value);
    }
  }

  /**
   * Calculate which fields have changed between original and updated row
   * Handles nested objects and flattened dot-notation fields
   */
  private calculateChangedFields(
    originalRow: any,
    updatedRow: any,
    rowId: GridRowId
  ): Record<string, any> {
    // Normalize the updated row to match the original row's nested structure
    const normalizedUpdatedRow = this.normalizeUpdatedRow(updatedRow);

    const changedFields: Record<string, any> = {};
    const idField = this.getIdFieldName(originalRow);

    // Always include the ID field for identification using the rowId parameter
    changedFields[idField] = rowId;

    // Compare each field to find changes (using normalized version)
    Object.keys(normalizedUpdatedRow).forEach((key) => {
      if (key === idField) return; // Already added

      const oldValue = originalRow[key];
      const newValue = normalizedUpdatedRow[key];

      // Check if this is a nested object
      const isNestedObject =
        typeof newValue === "object" &&
        newValue !== null &&
        !Array.isArray(newValue);

      if (isNestedObject) {
        // For nested objects, compare each property within and emit flat dot-notation keys
        const nestedChanges = this.compareNestedObject(oldValue, newValue);
        if (nestedChanges) {
          Object.entries(nestedChanges).forEach(([nestedKey, nestedVal]) => {
            changedFields[`${key}.${nestedKey}`] = nestedVal;
          });
        }
      } else {
        // For primitive values
        if (this.areValuesDifferent(oldValue, newValue)) {
          changedFields[key] = newValue;
        }
      }
    });

    return changedFields;
  }

  /**
   * Compare nested object properties and return only changed fields
   * Returns null if no changes, or an object with only changed properties
   */
  private compareNestedObject(
    oldObject: any,
    newObject: any
  ): Record<string, any> | null {
    if (!oldObject || typeof oldObject !== "object") {
      // If old value wasn't an object, return the entire new object
      return newObject;
    }

    const changes: Record<string, any> = {};
    let hasChanges = false;

    // Compare each property in the new object
    Object.keys(newObject).forEach((key) => {
      const oldValue = oldObject[key];
      const newValue = newObject[key];

      if (this.areValuesDifferent(oldValue, newValue)) {
        changes[key] = newValue;
        hasChanges = true;
      }
    });

    return hasChanges ? changes : null;
  }

  /**
   * Compare two values for equality (handles primitives, objects, arrays)
   */
  private areValuesDifferent(oldValue: any, newValue: any): boolean {
    // Strict equality check (handles same primitives, both null, both undefined, same reference)
    if (oldValue === newValue) return false;

    // If one is null/undefined but not both (since === check above failed)
    if (oldValue === null || newValue === null) return true;
    if (oldValue === undefined || newValue === undefined) return true;

    // Deep equality check for objects/arrays (both are non-null objects at this point)
    if (typeof oldValue === "object" && typeof newValue === "object") {
      return JSON.stringify(oldValue) !== JSON.stringify(newValue);
    }

    // Primitive comparison (shouldn't reach here due to === check, but kept for safety)
    return oldValue !== newValue;
  }

  /**
   * Prepare the data to send to the server based on configuration
   * @param rowId - The ID of the row being updated
   * @param updatedRow - The updated row data
   * @param previousRow - The previous row data (before the update)
   */
  prepareUpdateData(rowId: GridRowId, updatedRow: any, previousRow?: any): any {
    // If no previous row available, send the full updated row
    if (!previousRow) {
      if (this.config.onBeforeUpdate) {
        return this.config.onBeforeUpdate(updatedRow, updatedRow, updatedRow);
      }
      return updatedRow;
    }

    // Calculate changed fields if enabled
    if (this.config.submitOnlyChangedFields) {
      const changedFields = this.calculateChangedFields(
        previousRow,
        updatedRow,
        rowId
      );

      // Allow custom transformation via callback
      if (this.config.onBeforeUpdate) {
        return this.config.onBeforeUpdate(
          previousRow,
          updatedRow,
          changedFields
        );
      }

      return changedFields;
    } else {
      // Send full row but still allow transformation
      if (this.config.onBeforeUpdate) {
        return this.config.onBeforeUpdate(previousRow, updatedRow, updatedRow);
      }

      return updatedRow;
    }
  }

  /**
   * Send UPDATE request to the server
   */
  async sendUpdateRequest(data: any): Promise<UpdateResult> {
    try {
      const requestBody = {
        operation: "UPDATE",
        userId: getCurrentUserId(),
        data,
      };

      const response = await axios.post<CRUDResponse>(
        this.config.apiUrl,
        requestBody,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        return {
          success: true,
          data: Array.isArray(response.data.data)
            ? response.data.data[0]
            : response.data.data,
        };
      } else {
        return {
          success: false,
          error: response.data.errors?.join(", ") || "Save failed",
        };
      }
    } catch (error) {
      const errorMessage = axios.isAxiosError(error)
        ? error.response?.data?.message || error.message
        : error instanceof Error
        ? error.message
        : "Failed to save record";

      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  /**
   * Send DELETE request to the server
   */
  async sendDeleteRequest(id: GridRowId): Promise<UpdateResult> {
    try {
      const requestBody = {
        operation: "DELETE",
        userId: getCurrentUserId(),
        data: { id },
      };

      const response = await axios.post<CRUDResponse>(
        this.config.apiUrl,
        requestBody,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data,
        };
      } else {
        return {
          success: false,
          error: response.data.errors?.join(", ") || "Delete failed",
        };
      }
    } catch (error) {
      const errorMessage = axios.isAxiosError(error)
        ? error.response?.data?.message || error.message
        : error instanceof Error
        ? error.message
        : "Failed to delete record";

      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  /**
   * Send BULK_UPDATE request to the server
   * Performs batch string operations (append, prepend, remove, replace) on multiple rows
   */
  async sendBulkUpdateRequest(
    ids: (string | number)[],
    field: string,
    operations: BulkStringOperationConfig[]
  ): Promise<UpdateResult> {
    try {
      const requestBody = {
        operation: "BULK_UPDATE",
        userId: getCurrentUserId(),
        ids,
        field,
        operations,
      };

      const response = await axios.post<BulkUpdateResponse>(
        this.config.apiUrl,
        requestBody,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.data.errors?.join(", ") || "Bulk update failed",
        };
      }
    } catch (error) {
      const errorMessage = axios.isAxiosError(error)
        ? error.response?.data?.message || error.message
        : error instanceof Error
        ? error.message
        : "Failed to perform bulk update";

      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  /**
   * Send BULK_SET request to the server
   * Performs direct multi-field value assignment across multiple rows
   */
  async sendBulkSetRequest(
    ids: (string | number)[],
    fields: Record<string, any>
  ): Promise<UpdateResult> {
    try {
      const requestBody = {
        operation: "BULK_UPDATE",
        userId: getCurrentUserId(),
        ids,
        fields,
      };

      const response = await axios.post<BulkUpdateResponse>(
        this.config.apiUrl,
        requestBody,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.data.errors?.join(", ") || "Bulk set failed",
        };
      }
    } catch (error) {
      const errorMessage = axios.isAxiosError(error)
        ? error.response?.data?.message || error.message
        : error instanceof Error
        ? error.message
        : "Failed to perform bulk set";

      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  /**
   * Update the configuration (useful when props change)
   */
  updateConfig(config: Partial<CRUDConfig>): void {
    this.config = { ...this.config, ...config };
  }
}
